<script setup lang="ts">
import { computed } from 'vue';

import { BaseBadge, BaseButton, BaseIcon } from '@magma-app/magma-lapilli';

import type { ManageRewardsItem } from './types';

const props = defineProps<{
  item: ManageRewardsItem;
  title?: string | null;
  description?: string | null;
  showBuyButton: boolean;
  buyDisabled: boolean;
}>();

const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'buy'): void;
}>();

const resolvedTitle = computed(() => props.title || props.item.name);
const resolvedDescription = computed(
  () => props.description || props.item.description
);
</script>

<template>
  <div>
    <div class="relative">
      <img
        class="mb-8 h-[280px] w-full rounded-[5px] border border-transparent object-cover"
        :src="!item.pictureUrl ? '/img/reward_lg.png' : item.pictureUrl"
      />

      <BaseIcon
        class="absolute right-4 top-4"
        bg-color="#E2E8F0"
        color="#5F6C85"
        icon="close"
        hover-state
        @click="emit('close')"
      />
    </div>

    <div class="flex items-start justify-between">
      <h3
        class="mr-4 max-w-[80%] text-mgm-txt-lg font-medium text-foreground-emphasis"
      >
        {{ resolvedTitle }}
      </h3>

      <BaseBadge
        size="md"
        color="blue"
        :text="item.points"
        icon-type="icon"
        icon-name="star"
      />
    </div>

    <div
      class="my-4 flex items-start rounded-[5px] bg-background-subtle px-4 py-2"
    >
      <BaseIcon class="mr-2" icon="info" />

      <div class="bg-background-subtle text-mgm-txt-sm">
        <h4 class="font-medium text-foreground-emphasis">
          {{ $t('generic.retrieve') }}
        </h4>

        <p
          class="mt-1 font-normal text-foreground-default"
          v-html="item.retrievalInstruction"
        ></p>
      </div>
    </div>

    <p
      class="scrollbar-hide max-h-44 overflow-scroll text-mgm-txt-sm font-normal text-foreground-default"
      v-html="resolvedDescription"
    ></p>

    <BaseButton
      v-if="showBuyButton"
      class="mt-6 w-full"
      :disabled="buyDisabled"
      :text="$t('generic.buy')"
      @click="emit('buy')"
    />
  </div>
</template>
